import { useState } from "react";
import { useNavigationStore } from "../../store/navigationStore";
import {
  SUPPORTED_APP_LANGUAGES,
  SUPPORTED_VOICE_LANGUAGES,
} from "../../types/language";
import { CHAT_TYPE, SUPPORTED_MODE } from "../../types/navigation-mode";
import CloseButton from "../button/close-button";
import LanguageSelector from "../selector/language-selector";
import SettingItem from "./setting-item";
import SettingLanguage from "./setting-language";
import Text from "../text/text";
import { useTranslation } from "react-i18next";
import VoiceSelector from "../selector/voice-selector";
import { useLanguageStore } from "@/store/languageStore";
import ToggleButton from "../button/toggle-button";
import { useAppStore } from "@/store/appStore";
import packageJson from './../../../package.json';

interface SettingProps {
  onClose: () => void;
}

const SettingPage: React.FC<SettingProps> = ({ onClose }) => {
  const [isAppLanguageSelectorOpen, setIsAppLanguageSelectorOpen] =
    useState(false);
  const [isVoiceLanguageSelectorOpen, setIsVoiceLanguageSelectorOpen] =
    useState(false);
  const navMode = useNavigationStore((state) => state.navMode);
  const setNavMode = useNavigationStore((state) => state.setNavMode);
  const { t } = useTranslation();
  const getIconPath = (code: string) => `/assets/icon_${code}.png`;
  const [showDebugMenu, setShowDebugMenu] = useState(false);
  const debugMode = useAppStore((state) => state.debugMode);
  const setDebugMode = useAppStore((state) => state.setDebugMode);
  const [versionClick, setVersionClick] = useState(1);
  // const [isSimVoiceLanguageSelectorOpen, setIsSimVoiceLanguageSelectorOpen] =
  //   useState(false);
  const [isNavVoiceLanguageSelectorOpen, setIsNavVoiceLanguageSelectorOpen] =
    useState(false);
  const voiceNames = useLanguageStore((s) => s.voiceNames);

  return (
    <div className="flex justify-end h-full">
      <div className="hidden md:flex w-full bg-overlay"></div>
      <div className="settings w-full md:max-w-[393px] overflow-y-auto">
        <div className="flex items-center justify-between p-[20px]">
          <Text
            text="settings"
            className="font-medium primary-text-color text-2xl"
          />
          <CloseButton goBack={false} onClick={() => onClose()} />
        </div>
        <div className="flex flex-col gap-[8px] settings-bg mb-[12px]">
          <Text
            text="defaultMode"
            className="primary-text-color  p-[20px]"
            size={13}
          />
          {SUPPORTED_MODE.map((mode) => {
            return (
              <div key={mode.code}>
                <SettingItem
                  key={mode.code}
                  text={t(mode.code)}
                  icon={getIconPath(mode.code)}
                  selected={navMode === mode.code}
                  onClick={() => setNavMode(mode.code)}
                />
                {mode.code === "walk" ? (
                  <></>
                ) : //Not support avoid stairs yet
                  // <div className="flex justify-between px-[20px]">
                  //   <Text
                  //     text="avoid_stairs"
                  //     className="pl-[36px] text-white text-[15px]"
                  //   />
                  //   <ToggleButton
                  //     value={useStairs}
                  //     onChange={(value) => setUseStairs(value)}
                  //   />
                  // </div>
                  null}
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-[8px] p-[20px] settings-bg mb-[12px]">
          <Text
            text="preferredLanguage"
            className="primary-text-color"
            size={13}
          />
          <SettingLanguage
            text={t("appLanguage")}
            languageType="app"
            onClick={() => setIsAppLanguageSelectorOpen(true)}
          />
        </div>
        <div className="flex flex-col gap-[8px] p-[20px] settings-bg mb-[12px]">
          <Text
            text="preferredVoice"
            className="primary-text-color"
            size={13}
          />
          <SettingLanguage
            text={t("language")}
            languageType="voiceLang"
            onClick={() => setIsVoiceLanguageSelectorOpen(true)}
          />
          <SettingLanguage
            text={t("voice")}
            languageType="voice"
            onClick={() => setIsNavVoiceLanguageSelectorOpen(true)}
          />
          {/* For Testing */}
          {/* <SettingLanguage
            text="Simulation Voice"
            languageType="simulation"
            onClick={() => setIsSimVoiceLanguageSelectorOpen(true)}
          /> */}
        </div>

        <LanguageSelector
          isOpen={isAppLanguageSelectorOpen}
          onClose={() => setIsAppLanguageSelectorOpen(false)}
          languageType="app"
          languageList={SUPPORTED_APP_LANGUAGES}
        />

        <LanguageSelector
          isOpen={isVoiceLanguageSelectorOpen}
          onClose={() => setIsVoiceLanguageSelectorOpen(false)}
          languageType="voice"
          languageList={SUPPORTED_VOICE_LANGUAGES}
        />

        <VoiceSelector
          isOpen={isNavVoiceLanguageSelectorOpen}
          onClose={() => setIsNavVoiceLanguageSelectorOpen(false)}
          languageList={voiceNames}
          type={CHAT_TYPE.NAVIGATION}
        />

        {showDebugMenu && <div className="flex flex-col gap-[8px] p-[20px] settings-bg mb-[12px]">
          <Text
            text="debugMode"
            className="primary-text-color"
            size={13}
          />
          <div className="flex justify-between items-center">
            <Text
              text="enable_agent_selection"
              className="primary-text-color"
            />
            <ToggleButton
              value={debugMode}
              onChange={(value) => setDebugMode(value)}
            />
          </div>
        </div>}
        {/* For Testing */}
        {/* <VoiceSelector
          isOpen={isSimVoiceLanguageSelectorOpen}
          onClose={() => setIsSimVoiceLanguageSelectorOpen(false)}
          languageList={SIMULATION_VOICE}
          type={CHAT_TYPE.SIMULATION}
        /> */}
        <div className="flex gap-[8px] p-[20px] mb-[12px]" onClick={() => {
          setVersionClick(versionClick + 1);
          if (versionClick === 3) {
            setShowDebugMenu(true);
          }
        }}>
          <Text
            text="version"
            className="primary-text-color"
          />
          <Text
            text={`${packageJson.version}`}
            className="primary-text-color"
          />
        </div>
      </div>
    </div>
  );
};

export default SettingPage;
